import React, { useContext } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { AccountingContext } from "../../providers/AccountingProvider";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, DollarSign } from "lucide-react";
import { Button } from "../../components/ui/button";
import { cn, formatMonthYear, formatUniversalNumber } from "../../lib/utils";

const SummarySkeletonCard = () => (
  <div className="overflow-hidden border BookEZ_card_radius BookEZ_card_border_color shadow-lg animate-pulse BookEZ_card_bg">
    <div className="flex items-center justify-between p-4">
      <div className="flex justify-between gap-4 items-center">
        <div className="w-1.5 h-15 BookEZ_card_bg_secondary_light rounded-full" />
        <div>
          <div className="h-4 w-20 BookEZ_card_bg_secondary_light rounded mb-2" />
          <div className="h-6 w-32 BookEZ_card_bg_secondary_light rounded" />
        </div>
      </div>
      <div className="flex h-10 w-10 items-center justify-center rounded-full BookEZ_card_bg_secondary_light">
        <div className="h-5 w-5 BookEZ_card_bg_secondary_light rounded" />
      </div>
    </div>
  </div>
);

type StringOverrides = {
  title?: string;
  revenueLabel?: string;
  expensesLabel?: string;
  netProfitLabel?: string;
};

export const calculateNetProfit = (revenue: number, expenses: number) => {
  const normalizedExpenses = Math.abs(expenses);
  const netProfit = revenue - normalizedExpenses;

  // Optional: handeling for revenue = 0
  if (revenue === 0) {
    return formatUniversalNumber(-normalizedExpenses);
  }

  return formatUniversalNumber(netProfit);
};

const Summary: React.FC<{
  stringOverrides?: StringOverrides;
}> = ({
  stringOverrides: {
    title = "Dashboard",
    revenueLabel = "Revenue",
    expensesLabel = "Expenses",
    netProfitLabel = "Net Profit",
  } = {},
}) => {
  const {
    filteredTotalRevenue = 0,
    filteredTotalExpenses = 0,
    isSummaryLoading,
    handleResetToCurrentMonth,
    handlePreviousMonth,
    handleNextMonth,
    currentDate,
    isCurrentMonth,
  } = useContext(AccountingContext);

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center justify-between mb-6">
        <motion.h1
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="BookEZ_font_heading BookEZ_font_bold BookEZ_font_header_color"
          layout
        >
          {title}
        </motion.h1>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={handleResetToCurrentMonth}
            variant={"outline"}
            className={cn(
              "",
              isCurrentMonth() ? "BookEZ_btn_filled_hover_bg" : ""
            )}
          >
            Current Month
          </Button>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePreviousMonth}
              area-label="prev"
              variant="ghost"
              size="icon"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-3 py-1.5 flex-1 border BookEZ_font_color_gray_2  BookEZ_btn_ghost_border BookEZ_btn_radius text-center min-w-[120px] sm:min-w-[160px] truncate">
              {formatMonthYear(currentDate)}
            </div>
            <Button
              onClick={handleNextMonth}
              area-label="next"
              variant="ghost"
              size="icon"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
      {isSummaryLoading ? (
        <div className="grid md:grid-cols-3 gap-4">
          <SummarySkeletonCard />
          <SummarySkeletonCard />
          <SummarySkeletonCard />
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="overflow-hidden transition-all duration-200 border BookEZ_card_bg BookEZ_card_radius BookEZ_card_border_color shadow-lg">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex justify-between gap-4 items-center">
                  <div className="w-1.5 h-15 BookEZ_revenue_bg rounded-full" />
                  <div>
                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_2 BookEZ_font_light_bold">
                      {revenueLabel}
                    </p>
                    <h2 className="BookEZ_font_body_large BookEZ_font_color_gray_3 BookEZ_font_bold">
                      {formatUniversalNumber(filteredTotalRevenue)}
                    </h2>
                  </div>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center BookEZ_tab_radius BookEZ_revenue_bg_light`}
                >
                  <DollarSign className={`h-5 w-5 BookEZ_revenue_font_color`} />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="overflow-hidden transition-all duration-200 border BookEZ_card_bg BookEZ_card_radius BookEZ_card_border_color shadow-lg">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex justify-between gap-4 items-center">
                  <div className="w-1.5 h-15 BookEZ_expanse_bg rounded-full" />
                  <div>
                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_2 BookEZ_font_light_bold">
                      {expensesLabel}
                    </p>
                    <h2 className="BookEZ_font_body_large BookEZ_font_bold BookEZ_font_color_gray_3">
                      {formatUniversalNumber(filteredTotalExpenses) || 0.0}
                    </h2>
                  </div>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center BookEZ_tab_radius BookEZ_expanse_bg_light`}
                >
                  <DollarSign className={`h-5 w-5 BookEZ_expanse_font_color`} />
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card className="overflow-hidden transition-all duration-200 border BookEZ_card_bg BookEZ_card_radius BookEZ_card_border_color shadow-lg">
              <CardContent className="flex items-center justify-between p-4">
                <div className="flex justify-between gap-4 items-center">
                  <div className="w-1.5 h-15 BookEZ_profit_bg rounded-full" />
                  <div>
                    <p className="BookEZ_font_body_size BookEZ_font_color_gray_3 BookEZ_font_light_bold ">
                      {netProfitLabel}
                    </p>
                    <h2 className="BookEZ_font_body_large BookEZ_font_bold BookEZ_font_color_gray_3">
                      {calculateNetProfit(
                        filteredTotalRevenue,
                        filteredTotalExpenses
                      )}
                    </h2>
                  </div>
                </div>
                <div
                  className={`flex h-10 w-10 items-center justify-center BookEZ_tab_radius BookEZ_profit_bg_light`}
                >
                  <DollarSign className={`h-5 w-5 BookEZ_profit_font_color`} />
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Summary;
